<template>
    <div v-if="showRecorder" class="ovt__legend recorder" :class="{'with-config': configPanelOpen}">
        <span v-if="!onAir && !isConvertingToMp4" class="icon-cross" @click="closeRecorder()"></span>
        <button
            v-if="showRecorder"
            class="recorder-button"
            :disabled="isConvertingToMp4"
            :class="{ 'is-disabled': isConvertingToMp4 }"
            @click="toggleRecording()"
        >
            <template v-if="!onAir">
                <i class="record-icon" /> <span>{{ $t.startRecording }}</span>
            </template>
            <template v-else>
                <i class="record-icon stop" /> <span>{{ $t.stopRecording }}</span>
            </template>
        </button>
        <span v-if="isConvertingToMp4" class="recorder-notice">{{ conversionNotice }}</span>
        <span v-if="showConversionFailedNotice" class="recorder-notice">{{ $t.mp4ConversionFailedFallbackToWebm }}</span>
        <span v-if="showMaxDurationNotice" class="recorder-notice">{{ $t.maximumRecordingDurationReached }}</span>
        <span v-if="showNoDataNotice" class="recorder-notice">{{ $t.recordingNoData }}</span>
        <span v-if="onAir" class="timer">{{ timeFormatted() }}</span>
    </div>
</template>

<script>
import axios from 'axios'
import Config from '@/constants/config'
import VisualisationService from '@/services/visualisation.service'

export default {
    name: 'VisualisationRecorder',
    data() {
        return {
            canvas: null,
            stream: null,
            mediaRecorder: null,
            recordedBlobs: null,
            recordingMimeType: 'video/webm',
            recordingExtension: 'webm',
            maxRecordingDurationSeconds: Config.visualisation?.maxRecordingDurationSeconds || 600,
            recordingVideoBitsPerSecond: Config.visualisation?.recordingVideoBitsPerSecond || 5000000,
            time: 0,
            lastRecordedDurationSeconds: 0,
            timer: null,
            isStopping: false,
            finalizeTimer: null,
            isConvertingToMp4: false,
            conversionPhase: '',
            conversionProgress: null,
            showConversionFailedNotice: false,
            conversionFailedNoticeTimer: null,
            showMaxDurationNotice: false,
            maxDurationNoticeTimer: null,
            showNoDataNotice: false,
            noDataNoticeTimer: null,
        }
    },
    computed: {
        configPanelOpen() {
            return VisualisationService.getSelectObjectsShow();
        },
        showRecorder() {
            return VisualisationService.getOrbitRecorderShow();
        },
        onAir() {
            return VisualisationService.getOrbitRecorderOnAir();
        },
        preferredFormat() {
            return VisualisationService.getOrbitRecorderFormat() || 'webm';
        },
        conversionNotice() {
            if (this.conversionPhase === 'upload' || this.conversionPhase === 'convert') {
                return `${this.$t.mp4ConversionInProgress} ${this.creationProgressText()}`;
            }
            if (this.conversionPhase === 'download') {
                return `${this.$t.mp4DownloadInProgress} ${this.conversionProgressText()}`;
            }
            return this.$t.mp4ConversionInProgress;
        }
    },
    methods: {
        closeRecorder() {
            if (this.isConvertingToMp4) {
                return;
            }
            VisualisationService.setSettingsPanelName('');
            return VisualisationService.toggleRecorderShow();
        },
        toggleRecording() {
            if (this.isConvertingToMp4) {
                return;
            }
            if (!this.onAir) {
                const started = this.startRecording();
                if (started) {
                    VisualisationService.setOrbitRecorderOnAir(true);
                }
                return;
            }
            this.stopRecording();
            VisualisationService.setOrbitRecorderOnAir(false);
        },
        timeFormatted() {
            return `${this.formatTimerValue(this.time)} / ${this.formatTimerValue(this.maxRecordingDurationSeconds)}`;
        },
        formatTimerValue(timeInSeconds) {
            const minutes = Math.floor(timeInSeconds / 60);
            const seconds = timeInSeconds % 60;
            return `${minutes > 9 ? minutes : '0'+minutes}:${seconds > 9 ? seconds : '0'+seconds}`;
        },
        startRecording() {
            if (!this.stream || typeof MediaRecorder === 'undefined') {
                console.warn('[recorder] Cannot start recording: stream or MediaRecorder is unavailable.');
                return false;
            }

            const recordingFormat = this.resolveRecordingFormat(this.preferredFormat);
            if (!recordingFormat) {
                console.warn('[recorder] No supported recording mime type found for MP4/WEBM.');
                return false;
            }

            const options = {
                videoBitsPerSecond: this.recordingVideoBitsPerSecond
            };
            if (recordingFormat.mimeType) {
                options.mimeType = recordingFormat.mimeType;
            }

            this.recordedBlobs = [];
            this.recordingMimeType = recordingFormat.mimeType || `video/${recordingFormat.extension}`;
            this.recordingExtension = recordingFormat.extension;
            this.time = 0;
            this.lastRecordedDurationSeconds = 0;
            this.isStopping = false;
            this.isConvertingToMp4 = false;
            this.conversionPhase = '';
            this.conversionProgress = null;
            this.showConversionFailedNotice = false;
            this.showMaxDurationNotice = false;
            this.showNoDataNotice = false;

            try {
                this.mediaRecorder = new MediaRecorder(this.stream, options);
            } catch (error) {
                console.warn('[recorder] Failed to initialize MediaRecorder with selected format.', error);
                return false;
            }

            this.mediaRecorder.ondataavailable = this.handleDataAvailable;
            this.mediaRecorder.onstop = () => {
                // Firefox/Opera may dispatch final data asynchronously around "stop".
                this.scheduleFinalize(250);
            };
            this.mediaRecorder.onerror = () => {
                this.scheduleFinalize(0);
            };
            this.mediaRecorder.start(100);
            this.timer = setInterval(() => {
                if (this.isStopping) {
                    return;
                }
                this.time += 1;
                if (this.time >= this.maxRecordingDurationSeconds) {
                    this.displayMaxDurationNotice();
                    this.stopRecording();
                    VisualisationService.setOrbitRecorderOnAir(false);
                }
            }, 1000);
            return true;
        },
        handleDataAvailable(event) {
            if (event.data && event.data.size > 0) {
                this.recordedBlobs.push(event.data);
            }
            if (this.isStopping) {
                this.scheduleFinalize(200);
            }
        },
        stopRecording() {
            if (!this.mediaRecorder) {
                return;
            }
            this.lastRecordedDurationSeconds = this.time;
            this.time = 0;
            clearInterval(this.timer);
            this.timer = null;
            this.isStopping = true;
            if (this.mediaRecorder.state !== 'inactive') {
                this.scheduleFinalize(1200);
                this.mediaRecorder.stop();
            } else {
                this.scheduleFinalize(0);
            }
        },
        async download() {
            if (!this.recordedBlobs || !this.recordedBlobs.length) {
                this.displayNoDataNotice();
                return;
            }
            const tool = VisualisationService.getTool();
            const toolsTitles = {
                ovt: 'OrbitVisualisationTool',
                fvt: 'FlybyVisualisationTool',
                sovt: 'SynodicOrbitVisualisationTool'
            }
            const prefix = toolsTitles[tool];
            const extension = this.recordingExtension || this.getExtensionFromMimeType(this.recordingMimeType);
            const blobType = this.recordingMimeType || `video/${extension}`;
            const recordedBlob = new Blob(this.recordedBlobs, { type: blobType });
            const shouldConvertToMp4 = this.preferredFormat === 'mp4' && extension === 'webm';
            let outputBlob = recordedBlob;
            let outputExtension = extension;

            if (shouldConvertToMp4) {
                this.isConvertingToMp4 = true;
                try {
                    outputBlob = await this.convertWebmToMp4(recordedBlob);
                    outputExtension = 'mp4';
                } catch (error) {
                    console.warn('[recorder] MP4 conversion via backend failed. Falling back to WEBM download.', error);
                    this.displayConversionFailedNotice();
                } finally {
                    this.isConvertingToMp4 = false;
                }
            }

            const url = window.URL.createObjectURL(outputBlob);
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = url;
            a.download = `${prefix}_Record_${new Date().toISOString()}.${outputExtension}`;
            document.body.appendChild(a);
            a.click();
            setTimeout(() => {
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
                window.dispatchEvent(new Event('resize'));
            }, 100);
        },
        cleanupRecording() {
            clearTimeout(this.finalizeTimer);
            this.finalizeTimer = null;
            this.isStopping = false;
            this.mediaRecorder = null;
            this.isConvertingToMp4 = false;
            this.conversionPhase = '';
            this.conversionProgress = null;
            clearTimeout(this.conversionFailedNoticeTimer);
            this.conversionFailedNoticeTimer = null;
            this.showConversionFailedNotice = false;
            clearTimeout(this.maxDurationNoticeTimer);
            this.maxDurationNoticeTimer = null;
            this.showMaxDurationNotice = false;
            clearTimeout(this.noDataNoticeTimer);
            this.noDataNoticeTimer = null;
            this.showNoDataNotice = false;
        },
        displayConversionFailedNotice() {
            this.showConversionFailedNotice = true;
            clearTimeout(this.conversionFailedNoticeTimer);
            this.conversionFailedNoticeTimer = setTimeout(() => {
                this.showConversionFailedNotice = false;
            }, 5000);
        },
        displayMaxDurationNotice() {
            this.showMaxDurationNotice = true;
            clearTimeout(this.maxDurationNoticeTimer);
            this.maxDurationNoticeTimer = setTimeout(() => {
                this.showMaxDurationNotice = false;
            }, 5000);
        },
        displayNoDataNotice() {
            this.showNoDataNotice = true;
            clearTimeout(this.noDataNoticeTimer);
            this.noDataNoticeTimer = setTimeout(() => {
                this.showNoDataNotice = false;
            }, 5000);
        },
        scheduleFinalize(delay = 0) {
            clearTimeout(this.finalizeTimer);
            this.finalizeTimer = setTimeout(() => {
                this.finalizeRecording();
            }, delay);
        },
        async finalizeRecording() {
            if (!this.isStopping) {
                return;
            }
            const isInactive = !this.mediaRecorder || this.mediaRecorder.state === 'inactive';
            if (!isInactive) {
                this.scheduleFinalize(100);
                return;
            }
            await this.download();
            this.cleanupRecording();
        },
        async convertWebmToMp4(recordedBlob) {
            const url = `${Config.api.videoConverterUrl}/convert-webm-to-mp4`;
            const totalBytes = recordedBlob.size;
            const chunkSize = 4 * 1024 * 1024;
            const totalChunks = Math.max(1, Math.ceil(totalBytes / chunkSize));
            try {
                this.setConversionProgress('upload', 0);
                const createResponse = await axios.post(url, {
                    totalBytes,
                    totalChunks,
                    durationSeconds: this.lastRecordedDurationSeconds
                }, {
                    timeout: 30000
                });
                const chunkUrl = this.resolveVideoConverterUrl(createResponse?.data?.chunkUrl);
                const statusUrl = this.resolveVideoConverterUrl(createResponse?.data?.statusUrl);
                const downloadUrl = this.resolveVideoConverterUrl(createResponse?.data?.downloadUrl);
                if (!createResponse?.data?.jobId || !chunkUrl || !statusUrl || !downloadUrl) {
                    throw new Error('Backend returned an invalid conversion job response.');
                }
                await this.uploadConversionChunks(recordedBlob, chunkUrl, chunkSize, totalChunks);
                this.setConversionProgress('convert', 0);
                const job = await this.waitForMp4ConversionJob(statusUrl);
                if (job?.status !== 'completed') {
                    throw new Error(job?.error || 'Conversion job did not complete successfully.');
                }
                return await this.downloadConvertedMp4(downloadUrl);
            } catch (error) {
                const details = await this.extractAxiosErrorMessage(error);
                throw new Error(details);
            }
        },
        async uploadConversionChunks(recordedBlob, chunkUrl, chunkSize, totalChunks) {
            for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
                const start = chunkIndex * chunkSize;
                const end = Math.min(recordedBlob.size, start + chunkSize);
                const chunkBlob = recordedBlob.slice(start, end, recordedBlob.type);
                const completedBytesBeforeChunk = start;
                await this.uploadConversionChunkWithRetry(
                    recordedBlob,
                    chunkBlob,
                    chunkUrl,
                    chunkIndex,
                    totalChunks,
                    completedBytesBeforeChunk
                );
                const progress = Math.min(100, Math.round(((chunkIndex + 1) * 100) / totalChunks));
                this.setConversionProgress('upload', progress);
            }
        },
        async uploadConversionChunkWithRetry(recordedBlob, chunkBlob, chunkUrl, chunkIndex, totalChunks, completedBytesBeforeChunk) {
            const maxAttempts = 3;
            for (let attempt = 1; attempt <= maxAttempts; attempt++) {
                try {
                    await axios.post(`${chunkUrl}?index=${chunkIndex}&last=${chunkIndex === totalChunks - 1 ? '1' : '0'}`, chunkBlob, {
                        headers: {
                            'Content-Type': 'application/octet-stream',
                            'X-Chunk-Bytes': String(chunkBlob.size)
                        },
                        timeout: 300000,
                        onUploadProgress: (progressEvent) => {
                            const loaded = progressEvent?.loaded || 0;
                            const totalUploaded = Math.min(recordedBlob.size, completedBytesBeforeChunk + loaded);
                            const progress = Math.min(100, Math.round((totalUploaded * 100) / recordedBlob.size));
                            this.setConversionProgress('upload', progress);
                        }
                    });
                    return;
                } catch (error) {
                    const completedProgress = Math.min(100, Math.round((completedBytesBeforeChunk * 100) / recordedBlob.size));
                    this.setConversionProgress('upload', completedProgress);
                    if (attempt === maxAttempts || !this.isRetryableChunkUploadError(error)) {
                        throw error;
                    }
                    await this.sleep(1000 * attempt);
                }
            }
        },
        isRetryableChunkUploadError(error) {
            const status = error?.response?.status;
            if (!error?.response) {
                return true;
            }
            return status === 408 || status === 429 || status >= 500;
        },
        async waitForMp4ConversionJob(statusUrl) {
            const deadline = Date.now() + 600000;
            while (Date.now() < deadline) {
                const response = await axios.get(statusUrl, {
                    timeout: 30000
                });
                const job = response?.data;
                if (!job || !job.status) {
                    throw new Error('Backend returned an invalid conversion job status.');
                }
                if (job.status === 'uploading' || job.status === 'queued') {
                    this.setConversionProgress('upload', job.uploadProgress ?? this.conversionProgress);
                } else if (job.status === 'converting') {
                    this.setConversionProgress('convert', job.conversionProgress ?? this.conversionProgress);
                }
                if (job.status === 'completed' || job.status === 'failed') {
                    return job;
                }
                await this.sleep(2000);
            }
            throw new Error('Conversion job polling timed out.');
        },
        async downloadConvertedMp4(downloadUrl) {
            this.setConversionProgress('download', 0);
            const response = await axios.get(downloadUrl, {
                responseType: 'blob',
                timeout: 600000,
                onDownloadProgress: (progressEvent) => {
                    const total = progressEvent?.total || 0;
                    if (!total) {
                        return;
                    }
                    this.setConversionProgress('download', Math.min(100, Math.round((progressEvent.loaded * 100) / total)));
                }
            });
            if (!response || !response.data || response.data.size === 0) {
                throw new Error('Backend returned empty MP4 payload.');
            }
            this.setConversionProgress('download', 100);
            return response.data;
        },
        resolveVideoConverterUrl(path) {
            if (!path) {
                return null;
            }
            if (/^https?:\/\//.test(path)) {
                return path;
            }
            return `${Config.api.videoConverterUrl}${path}`;
        },
        sleep(duration) {
            return new Promise((resolve) => {
                setTimeout(resolve, duration);
            });
        },
        setConversionProgress(phase, progress) {
            this.conversionPhase = phase;
            this.conversionProgress = typeof progress === 'number' ? progress : null;
        },
        creationProgressText() {
            if (this.conversionPhase === 'upload') {
                if (this.conversionProgress === null) {
                    return '';
                }
                return `${Math.min(70, Math.round(this.conversionProgress * 0.7))}%`;
            }
            if (this.conversionPhase === 'convert') {
                if (this.conversionProgress === null) {
                    return '70%';
                }
                return `${Math.min(100, 70 + Math.round(this.conversionProgress * 0.3))}%`;
            }
            return this.conversionProgressText();
        },
        conversionProgressText() {
            return this.conversionProgress === null ? '' : `${this.conversionProgress}%`;
        },
        async extractAxiosErrorMessage(error) {
            if (error && !error.response && error.message) {
                return error.message;
            }
            const status = error?.response?.status;
            const data = error?.response?.data;
            if (!data) {
                return `HTTP ${status || 'unknown'} (no response body).`;
            }
            if (data instanceof Blob) {
                try {
                    const payloadText = await data.text();
                    try {
                        const payloadJson = JSON.parse(payloadText);
                        return `HTTP ${status || 'unknown'}: ${payloadJson.error || payloadText}`;
                    } catch (parseError) {
                        return `HTTP ${status || 'unknown'}: ${payloadText}`;
                    }
                } catch (blobError) {
                    return `HTTP ${status || 'unknown'} (unable to read response body).`;
                }
            }
            if (typeof data === 'object') {
                return `HTTP ${status || 'unknown'}: ${data.error || JSON.stringify(data)}`;
            }
            return `HTTP ${status || 'unknown'}: ${String(data)}`;
        },
        resolveRecordingFormat(preferredFormat) {
            const effectivePreferredFormat = this.getEffectivePreferredFormat(preferredFormat);
            const preferredCandidates = this.getMimeCandidates(effectivePreferredFormat);
            const preferredMimeType = preferredCandidates.find((mimeType) => this.isMimeTypeSupported(mimeType));
            if (preferredMimeType) {
                return {
                    mimeType: preferredMimeType,
                    extension: this.getExtensionFromMimeType(preferredMimeType)
                };
            }

            const fallbackFormat = preferredFormat === 'mp4' ? 'webm' : 'mp4';
            const fallbackCandidates = this.getMimeCandidates(fallbackFormat);
            const fallbackMimeType = fallbackCandidates.find((mimeType) => this.isMimeTypeSupported(mimeType));
            if (fallbackMimeType) {
                return {
                    mimeType: fallbackMimeType,
                    extension: this.getExtensionFromMimeType(fallbackMimeType)
                };
            }

            return null;
        },
        getEffectivePreferredFormat(preferredFormat) {
            // Harmonized flow: always capture WEBM for MP4 requests and convert afterwards.
            if (preferredFormat === 'mp4') {
                return 'webm';
            }
            return preferredFormat;
        },
        getMimeCandidates(format) {
            if (format === 'mp4') {
                return [
                    'video/mp4;codecs="avc1.42E01E"',
                    'video/mp4'
                ];
            }
            return [
                'video/webm',
                'video/webm;codecs=vp8',
                'video/webm;codecs=vp9'
            ];
        },
        isMimeTypeSupported(mimeType) {
            if (typeof MediaRecorder === 'undefined') {
                return false;
            }
            if (typeof MediaRecorder.isTypeSupported !== 'function') {
                return true;
            }
            return MediaRecorder.isTypeSupported(mimeType);
        },
        getExtensionFromMimeType(mimeType) {
            return mimeType && mimeType.startsWith('video/mp4') ? 'mp4' : 'webm';
        },
    },
    mounted() {
        this.canvas = document.querySelector('.vt__simulation canvas');
        if (this.canvas){
            this.stream = this.canvas.captureStream();
        }
    },
    beforeUnmount() {
        clearInterval(this.timer);
        this.timer = null;
        clearTimeout(this.finalizeTimer);
        this.finalizeTimer = null;
        clearTimeout(this.conversionFailedNoticeTimer);
        this.conversionFailedNoticeTimer = null;
        clearTimeout(this.maxDurationNoticeTimer);
        this.maxDurationNoticeTimer = null;
        clearTimeout(this.noDataNoticeTimer);
        this.noDataNoticeTimer = null;
    }
}
</script>

<style>
.recorder-button.is-disabled {
    opacity: 0.65;
    cursor: not-allowed;
}
</style>
